import { useMutation, useQuery } from "@apollo/client";
import moment from "moment";
import { useEffect, useState } from "react";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Heading from "@components/heading/heading";
import Loading from "@components/loading/loading";
import SettingsLayout from "@components/settings-layout/settings-layout";

import { GET_AI_BRAIN, UPDATE_AI_BRAIN_CONSOLIDATED } from "./queries";

const AiMemory = () => {
  const isFlagOn = waffle.flag_is_active("ask-ai-memory");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, loading } = useQuery<any>(GET_AI_BRAIN, {
    fetchPolicy: "network-only",
    skip: !isFlagOn,
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [updateBrain, { loading: saving }] = useMutation<any>(
    UPDATE_AI_BRAIN_CONSOLIDATED,
  );
  const [draft, setDraft] = useState<string>("");
  const [editing, setEditing] = useState<boolean>(false);

  const brain = data?.me?.aiBrain;

  useEffect(() => {
    if (brain && !editing) {
      setDraft(brain.consolidated ?? "");
    }
  }, [brain, editing]);

  if (!isFlagOn) {
    return <div>Feature unavailable.</div>;
  }

  if (loading && !brain) return <Loading />;

  const lastRefreshed = brain?.lastDreamAt
    ? moment(brain.lastDreamAt).fromNow()
    : "Never";

  const onSave = async () => {
    try {
      await updateBrain({ variables: { content: draft } });
      successNotificationVar({ title: "AI Memory saved" });
      setEditing(false);
    } catch {
      errorNotificationVar({ title: "Could not save AI Memory" });
    }
  };

  const onCancel = () => {
    setDraft(brain?.consolidated ?? "");
    setEditing(false);
  };

  const liveNotes = (brain?.liveNotes ?? "").trim();

  return (
    <div>
      <Heading small title="AI Memory" />

      <SettingsLayout.Container>
        <SettingsLayout.Row>
          <SettingsLayout.Left>About</SettingsLayout.Left>
          <SettingsLayout.Right>
            <p className="text-sm text-gray-600">
              AskAI keeps a private memory of soft context about you. It updates
              as you chat and gets reorganized every night. You can edit the
              main section any time.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <strong>Last refreshed:</strong> {lastRefreshed}
            </p>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Your brain</SettingsLayout.Left>
          <SettingsLayout.Right>
            {editing ? (
              <div className="w-full space-y-3">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={20}
                  className="w-full border border-gray-300 rounded p-2 font-mono text-sm"
                  maxLength={8000}
                />
                <div className="flex items-center gap-2">
                  <Button
                    text="Save"
                    theme={buttonTheme.primary}
                    onClick={onSave}
                    disabled={saving}
                  />
                  <Button text="Cancel" onClick={onCancel} disabled={saving} />
                  {saving && <Loading mini size="4" />}
                </div>
              </div>
            ) : (
              <div className="w-full space-y-3">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 rounded p-3">
                  {brain?.consolidated ||
                    "Your AI memory is empty. It will start filling in as you use AskAI."}
                </pre>
                <Button text="Edit" onClick={() => setEditing(true)} />
              </div>
            )}
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>
            Recent observations
            <div className="text-xs text-gray-500 font-normal mt-1">
              Since last refresh
            </div>
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            {liveNotes ? (
              <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 rounded p-3">
                {liveNotes}
              </pre>
            ) : (
              <p className="text-sm text-gray-500">
                No new observations since last refresh.
              </p>
            )}
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      </SettingsLayout.Container>
    </div>
  );
};

export default AiMemory;
